import { useMemo, useState } from "react";

import {
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";

import { NetworkSearchBar } from "components/Network/NetworkSearchBar";
import { NetworkSortMenu } from "components/Network/NetworkSortMenu";
import { filterAndSortNetworkItems } from "utils/network";

import { NetworkCarrierTableHead } from "./NetworkCarrierTableHead";
import { ShipperNetworkTableRow } from "./ShipperNetworkTableRow";

import type { NetworkSortType } from "components/Network/NetworkSortMenu";

import type { MyCarrierItem } from "./types";

interface Props {
  loading: boolean;
  items: MyCarrierItem[];
  error: string | null;
  onRemove: (item: MyCarrierItem) => void;
  removingKey: string | null;
}

export function ShipperNetworkTable(props: Props) {
  const { loading, items, error, onRemove, removingKey } = props;

  const [sortType, setSortType] = useState<NetworkSortType>("CONTACT_NAME");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredAndSorted = useMemo(
    () => filterAndSortNetworkItems(items, sortType, searchQuery),
    [items, sortType, searchQuery],
  );

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack>
        <Typography color="red">{error}</Typography>
      </Stack>
    );
  }
  if (items.length === 0) {
    return (
      <Stack>
        <Typography variant="h6">
          You haven&apos;t added any carriers yet.
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ width: { xs: "100%", sm: 440 } }}>
          <NetworkSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by contact or company name"
          />
        </Box>
        <NetworkSortMenu value={sortType} onChange={setSortType} />
      </Stack>
      <Box sx={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <NetworkCarrierTableHead />
            <TableBody>
              {filteredAndSorted.map((item) => (
                <ShipperNetworkTableRow
                  key={item.key}
                  item={item}
                  onRemove={onRemove}
                  removing={item.key === removingKey}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}
